/** A taxonomy term from the feed — `slug` drives logic, `name` is displayed. */
export interface ChangelogTerm {
  slug: string
  name: string
}

export interface ChangelogReleaseConstructor {
  id: number
  /** Plain text — the feed's HTML title reduced to text. */
  title: string
  /** Sanitized HTML — safe to render via `dangerouslySetInnerHTML`. */
  excerpt: string
  /** Full post body as sanitized HTML. Falls back to the excerpt when the feed omits it. */
  body: string
  url: string
  publishedAt: string
  imageUrl: Nullable<string>
  imageAlt: Nullable<string>
  changeType: Nullable<ChangelogTerm>
  productCategory: Nullable<ChangelogTerm>
}

/**
 * A single release entry from Marketing's public changelog. Sourced from the
 * marketing site rather than the Procurify backend, so every `_embedded`
 * derived field is optional — a post can be published without a hero image, or
 * without the `change_type` / `product_category` taxonomies applied.
 */
export class ChangelogRelease {
  readonly id: number
  readonly title: string
  readonly excerpt: string
  readonly body: string
  readonly url: string
  readonly publishedAt: string
  readonly imageUrl: Nullable<string>
  readonly imageAlt: Nullable<string>
  readonly changeType: Nullable<ChangelogTerm>
  readonly productCategory: Nullable<ChangelogTerm>

  constructor(data: ChangelogReleaseConstructor) {
    this.id = data.id
    this.title = data.title
    this.excerpt = data.excerpt
    this.body = data.body
    this.url = data.url
    this.publishedAt = data.publishedAt
    this.imageUrl = data.imageUrl
    this.imageAlt = data.imageAlt
    this.changeType = data.changeType
    this.productCategory = data.productCategory
  }

  get hasHeroImage(): boolean {
    return !!this.imageUrl?.trim()
  }

  get publishedAtTimestamp(): number {
    return new Date(this.publishedAt).getTime()
  }
}
